import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { PlayIcon } from '@fronton/icons-react';
import { useSelector } from 'react-redux';
import { getDisplayedName } from 'features/product-info/ducks/selectors';
import { transformVideoThumb } from 'utils/transform-video-thumb';
import { isFirefox } from 'utils';
import { videoContainer, playIconWrapper, playIcon, video } from './styles';
import {
  DATA_QA_PREVIEW_VIDEO,
  DATA_QA_PREVIEW_VIDEO_SOURCE,
  DATA_QA_PLAY_ICON_WRAP,
  DATA_QA_PLAY_VIDEO,
  DATA_QA_MEDIA_PREVIEW_VIDEO,
} from '../constants';

type Props = {
  src: string;
  onMediaLoad?: (mediaType: string, imgSrc: string, loadTime?: number) => void;
};

const POSTER_WIDTH = 400;

export const VideoMobile: FC<Props> = ({ src, onMediaLoad }) => {
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState<boolean>(false);
  const thumbSrc = useOptimisedImages({
    src: transformVideoThumb(src),
    sizes: {
      src: {
        w: POSTER_WIDTH,
      },
    },
    options: {
      q: '80',
      c: 'fill',
    },
  }).src.x;

  const ref = useRef<HTMLVideoElement>(null);
  const productName = useSelector(getDisplayedName) || '';
  const isFF = useMemo(() => {
    if (typeof navigator !== 'undefined') {
      return isFirefox(navigator?.userAgent);
    }

    return false;
  }, []);

  // react known issue "video element + muted attribute"
  // https://github.com/facebook/react/issues/10389
  useEffect(() => {
    ref.current?.setAttribute('muted', '');
  }, []);

  const videoPlayCb = useCallback(() => {
    if (!isMediaLoaded) {
      onMediaLoad?.('mediaVideo', src);
      setIsMediaLoaded(true);
    }
    setIsStarted(true);
  }, [isMediaLoaded, onMediaLoad, src]);

  const playVideo = useCallback(() => {
    ref.current?.play();
    setIsStarted(true);
  }, []);

  const pauseVideo = useCallback(() => {
    ref.current?.pause();
    setIsStarted(false);
  }, []);

  return (
    <>
      <picture data-qa={DATA_QA_MEDIA_PREVIEW_VIDEO} className={videoContainer}>
        {!isStarted && !isFF && (
          <div data-qa={DATA_QA_PLAY_ICON_WRAP} className={playIconWrapper} onClick={playVideo}>
            <PlayIcon
              className={playIcon}
              type="fill"
              color="background-secondary"
              size="l"
              data-is-playing={`${isStarted}`}
              data-qa={DATA_QA_PLAY_VIDEO}
            />
          </div>
        )}
        <video
          onTouchMove={pauseVideo}
          preload="none"
          playsInline={true}
          data-qa={DATA_QA_PREVIEW_VIDEO}
          title={productName}
          onPlay={videoPlayCb}
          poster={thumbSrc}
          ref={ref}
          className={video}
          controls={true}
          muted={true}
          onClick={() => {
            setIsStarted((prev) => !prev);
          }}
        >
          <source src={src} type="video/mp4" data-qa={DATA_QA_PREVIEW_VIDEO_SOURCE} />
        </video>
      </picture>
    </>
  );
};
